import { trackEvent } from 'helpers/Analytics';
import {
  PRE_SIGNUP_AUDIENCE_STORAGE_KEY,
  PRE_CHECKOUT_AUDIENCE_STORAGE_KEY,
} from 'constants/constants';

export const AUDIENCES = {
  CLIENT_UNCLASSIFIED_REGISTERED_NO_JOB_60D:
    'client_unclassified_registered_no_job_60d',
  FUNDED_STARTUP_REGISTERED_NO_JOB_60D: 'funded_startup_registered_no_job_60d',
  CONSUMER_BRAND_REGISTERED_NO_JOB_60D: 'consumer_brand_registered_no_job_60d',
  FREELANCER_REGISTERED_NOT_PRO_30D: 'freelancer_registered_not_pro_30d',
  FREELANCER_PRO: 'freelancer_pro',
};

const AUDIENCE_TRANSITION_DEDUPE_PREFIX = 'twine_audience_transition_logged';

function transitionDedupeKey(userId, fromAudience, toAudience) {
  return `${AUDIENCE_TRANSITION_DEDUPE_PREFIX}:${userId}|${
    fromAudience || ''
  }|${toAudience || ''}`;
}

function hasLoggedTransition(userId, fromAudience, toAudience) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return false;
  }
  return (
    sessionStorage.getItem(
      transitionDedupeKey(userId, fromAudience, toAudience)
    ) === '1'
  );
}

function markTransitionLogged(userId, fromAudience, toAudience) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return;
  }
  try {
    sessionStorage.setItem(
      transitionDedupeKey(userId, fromAudience, toAudience),
      '1'
    );
  } catch (e) {
    // private mode / quota
  }
}

export function trackAudienceTransition(
  fromAudience,
  toAudience,
  userId = null
) {
  if (userId != null && hasLoggedTransition(userId, fromAudience, toAudience)) {
    return false;
  }

  if (fromAudience) {
    trackEvent(`remove_audience_${fromAudience}`);
  }
  if (toAudience) {
    trackEvent(`add_audience_${toAudience}`);
  }

  if (userId != null) {
    markTransitionLogged(userId, fromAudience, toAudience);
  }

  return true;
}

export function storePreSignupAudience(audience) {
  if (
    typeof sessionStorage === 'undefined' ||
    audience == null ||
    audience === ''
  ) {
    return;
  }
  try {
    sessionStorage.setItem(PRE_SIGNUP_AUDIENCE_STORAGE_KEY, String(audience));
  } catch (e) {
    // private mode / quota
  }
}

export function consumePreSignupAudience() {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(PRE_SIGNUP_AUDIENCE_STORAGE_KEY);
    if (raw) {
      sessionStorage.removeItem(PRE_SIGNUP_AUDIENCE_STORAGE_KEY);
    }
    return raw || null;
  } catch (e) {
    return null;
  }
}

export function storePreCheckoutAudience({ userId, audience }) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return;
  }
  try {
    sessionStorage.setItem(
      PRE_CHECKOUT_AUDIENCE_STORAGE_KEY,
      JSON.stringify({
        userId,
        audience: audience || AUDIENCES.FREELANCER_REGISTERED_NOT_PRO_30D,
      })
    );
  } catch (e) {
    // private mode / quota
  }
}

function consumePreCheckoutAudience(userId) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(PRE_CHECKOUT_AUDIENCE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed.userId !== userId) {
      return null;
    }
    sessionStorage.removeItem(PRE_CHECKOUT_AUDIENCE_STORAGE_KEY);
    return parsed.audience || null;
  } catch (e) {
    sessionStorage.removeItem(PRE_CHECKOUT_AUDIENCE_STORAGE_KEY);
    return null;
  }
}

export function maybeTrackAudienceTransitionOnSignup({ user }) {
  if (!user || user.id == null) {
    return false;
  }

  const preSignupAudience = consumePreSignupAudience();

  if (user.type === 'seller') {
    if (user.audience !== AUDIENCES.FREELANCER_REGISTERED_NOT_PRO_30D) {
      return false;
    }

    return trackAudienceTransition(
      preSignupAudience,
      AUDIENCES.FREELANCER_REGISTERED_NOT_PRO_30D,
      user.id
    );
  }

  if (user.type === 'buyer') {
    if (user.audience === AUDIENCES.FUNDED_STARTUP_REGISTERED_NO_JOB_60D) {
      return trackAudienceTransition(
        preSignupAudience,
        AUDIENCES.FUNDED_STARTUP_REGISTERED_NO_JOB_60D,
        user.id
      );
    }

    if (user.audience === AUDIENCES.CONSUMER_BRAND_REGISTERED_NO_JOB_60D) {
      return trackAudienceTransition(
        preSignupAudience,
        AUDIENCES.CONSUMER_BRAND_REGISTERED_NO_JOB_60D,
        user.id
      );
    }

    if (user.audience === AUDIENCES.CLIENT_UNCLASSIFIED_REGISTERED_NO_JOB_60D) {
      return trackAudienceTransition(
        preSignupAudience,
        AUDIENCES.CLIENT_UNCLASSIFIED_REGISTERED_NO_JOB_60D,
        user.id
      );
    }

    return false;
  }

  return false;
}

export function maybeTrackAudienceChange({ previousUser, user }) {
  if (!user || user.id == null) {
    return false;
  }

  const prevAudience = previousUser?.audience ?? null;
  const nextAudience = user.audience ?? null;

  if (prevAudience === nextAudience) {
    return false;
  }

  return trackAudienceTransition(prevAudience, nextAudience, user.id);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function maybeTrackAudienceTransitionOnSubscriptionSuccess({
  user,
  locationSearch,
  reloadAuth,
}) {
  if (typeof window === 'undefined') {
    return false;
  }
  if (!user || user.id == null) {
    return false;
  }

  const hasSuccess = new URLSearchParams(locationSearch || '').has('success');
  if (!hasSuccess) {
    return false;
  }

  let currentUser = user;

  if (typeof reloadAuth === 'function') {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const refreshed = await reloadAuth();
      if (refreshed) {
        currentUser = refreshed;
      }
      if (
        currentUser.pro &&
        currentUser.audience === AUDIENCES.FREELANCER_PRO
      ) {
        break;
      }
      if (attempt < 4) {
        await sleep(1000);
      }
    }
  }

  if (currentUser.type !== 'seller' || !currentUser.pro) {
    return false;
  }
  if (currentUser.audience !== AUDIENCES.FREELANCER_PRO) {
    return false;
  }

  const fromAudience =
    consumePreCheckoutAudience(currentUser.id) ||
    AUDIENCES.FREELANCER_REGISTERED_NOT_PRO_30D;

  return trackAudienceTransition(
    fromAudience,
    AUDIENCES.FREELANCER_PRO,
    currentUser.id
  );
}
